import { base64 } from '../../shared/crypto';
import type { VaultItem } from '../../shared/model';

export type VaultFile = NonNullable<VaultItem['file']>;

export const MAX_VAULT_FILE_BYTES = 5 * 1024 * 1024;

export async function readVaultFile(file: File): Promise<VaultFile> {
  if (file.size > MAX_VAULT_FILE_BYTES) {
    throw new Error('Choose a file up to 5 MB.');
  }

  // Browser MIME values may be empty or contain parameters; store a canonical data URL.
  const type = file.type.split(';')[0].trim().toLowerCase() || 'application/octet-stream';

  const safeType = /^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+-]+$/.test(type)
    ? type
    : 'application/octet-stream';

  const bytes = new Uint8Array(await file.arrayBuffer());

  return { name: file.name, type: safeType, data: `data:${safeType};base64,${base64(bytes)}` };
}

export function vaultFileObject(file: VaultFile): File {
  const binary = atob(file.data.slice(file.data.indexOf(',') + 1));

  return new File([Uint8Array.from(binary, char => char.charCodeAt(0))], file.name, {
    type: file.type || 'application/octet-stream',
  });
}

export function downloadVaultFile(file: VaultFile) {
  const url = URL.createObjectURL(vaultFileObject(file));
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = file.name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function isVaultImage(file: VaultFile) {
  return /^data:image\/(png|jpeg|webp|gif);base64,/.test(file.data);
}
